import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogoLockup } from "../components/brand/LogoLockup";
import { Button } from "../components/brand/Button";
import { BrandFooter } from "../components/brand/Footer";
import {
  CinematicCanvas, MonogramWatermark, HudDot, CornerBrackets, Eyebrow,
  ScrollCue, Reveal, DrawRule, HeroParallax, HudStatusBar,
} from "../components/brand/Cinematic";

const SERVICES = [
  { n: "01", title: "Brand & Positioning",
    subhead: "Identity. Built quietly.",
    body: "Page architecture, pricing ladder, content direction, and audience positioning engineered for long-term subscriber retention." },
  { n: "02", title: "Fan Engagement Operations",
    subhead: "Audience. Curated.",
    body: "A trained team manages direct messaging twenty-four hours a day in the creator's voice, with conversion playbooks tuned to lifetime fan value." },
  { n: "03", title: "Audience Growth",
    subhead: "Reach. By design.",
    body: "Acquisition funnels across Reddit, X, TikTok, Instagram, and Snap — paid promotion, organic campaigns, and creator collaborations." },
  { n: "04", title: "Partnerships & Diversification",
    subhead: "Economics. Without spectacle.",
    body: "Sponsorships, brand deals, and adjacent revenue streams that compound the creator's monthly take beyond the platform itself." },
];

const PROCESS = [
  { n: "01", label: "Invitation",  body: "Selective and quiet." },
  { n: "02", label: "Audit",       body: "A private read of your business." },
  { n: "03", label: "Strategy",    body: "Brand, content, economics." },
  { n: "04", label: "Continuity",  body: "Ongoing representation." },
];

export default function Landing() {
  useEffect(() => { document.title = "Omertàbella · Private Influence. Public Power."; }, []);
  return (
    <main data-testid="landing-page" className="relative min-h-screen bg-brand-bg text-brand-text-warm">
      <CinematicCanvas />

      {/* HUD top bar */}
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-6 pt-8 sm:px-10">
        <LogoLockup variant="header" as={Link} to="/" data-testid="landing-logo-link" />
        <div className="flex items-center gap-6">
          <HudStatusBar />
          <Link to="/login" data-testid="landing-enter-link"
                className="font-mono text-[11px] uppercase tracking-[0.42em] text-brand-gold transition-colors hover:text-brand-gold-light">
            Enter
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <MonogramWatermark className="bottom-[-12%] right-[-18%]" />

        {/* Official brand lockup — sits ALONE at the top of the hero, no parallax, no overlays. */}
        <div className="relative z-10 mx-auto flex max-w-7xl justify-center px-6 pt-16 sm:px-10 sm:pt-24 lg:pt-32">
          <Reveal>
            <LogoLockup variant="hero" glow />
          </Reveal>
        </div>

        <div className="relative mx-auto grid max-w-7xl gap-10 px-6 pt-32 sm:px-10 lg:grid-cols-12 lg:pt-40">
          <HeroParallax className="lg:col-span-12">
            <Reveal>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.42em] text-brand-gold font-mono">
                <span className="inline-block h-px w-10 bg-brand-gold/60" />
                Atlanta, Georgia · Est. 2025 · By Invitation
                <HudDot crimson className="ml-2" />
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <h1 className="mt-10 font-display font-normal text-brand-text text-balance leading-[0.96]"
                  style={{ fontSize: "clamp(56px, 9vw, 144px)", letterSpacing: "-0.04em" }}>
                Private<br/>Influence.<br/>
                <em className="italic text-brand-gold">Public Power.</em>
              </h1>
            </Reveal>

            <Reveal delay={0.25}>
              <DrawRule className="mt-12" width="w-20" />
              <p className="mt-10 max-w-xl text-base leading-relaxed text-brand-text/70 sm:text-lg">
                A private management and marketing agency representing top-tier digital creators. We engineer the strategy, operations, and growth infrastructure that scales content into revenue — and revenue into a long-term brand. Every engagement is confidential by design.
              </p>
            </Reveal>

            <Reveal delay={0.4}>
              <div
                data-testid="hero-cta-row"
                className="mt-12 flex flex-col items-center justify-center gap-y-6 sm:flex-row sm:flex-wrap sm:items-end sm:justify-center sm:gap-x-6"
              >
                <div className="flex flex-col items-center sm:items-start">
                  <Button as={Link} to="/apply" data-testid="hero-apply-cta" variant="outline-gold" size="lg">
                    Request an Invitation
                  </Button>
                  <p
                    data-testid="hero-cta-subtitle"
                    className="mt-3 select-none text-center sm:text-left"
                    style={{
                      fontFamily: "Inter, system-ui, sans-serif",
                      fontWeight: 400,
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: "0.2em",
                      color: "rgba(245,240,232,0.55)",
                    }}
                  >
                    Replies issued within 7 days.
                  </p>
                </div>
                <Button as={Link} to="/login" data-testid="hero-roster-cta" variant="secondary" size="lg">Roster Access</Button>
              </div>
            </Reveal>
          </HeroParallax>
        </div>

        <div className="relative mt-24 flex justify-center pb-12 lg:mt-40">
          <ScrollCue />
        </div>
      </section>

      {/* Manifesto quote slab — sits between hero and process */}
      <section
        data-testid="manifesto-slab"
        className="relative overflow-hidden"
        style={{ paddingTop: "clamp(120px, 14vh, 200px)", paddingBottom: "clamp(120px, 14vh, 200px)" }}
      >
        {/* Faint gold radial glow behind */}
        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 -z-10 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            width: 600,
            height: 600,
            background:
              "radial-gradient(circle, rgba(201,168,76,0.12) 0%, rgba(201,168,76,0.04) 40%, transparent 75%)",
            filter: "blur(60px)",
          }}
        />
        <Reveal>
          <div className="mx-auto flex max-w-5xl flex-col items-center px-6 sm:px-10">
            {/* Top hairline */}
            <span
              aria-hidden
              className="block h-px"
              style={{ width: 80, backgroundColor: "rgba(201,168,76,0.3)" }}
            />
            <blockquote
              data-testid="manifesto-quote"
              className="my-12 text-center font-display italic font-normal"
              style={{
                fontSize: "clamp(28px, 5.5vw, 80px)",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                color: "#F5F0E8",
              }}
            >
              We do not seek volume. We seek{" "}
              <span style={{ color: "#C9A84C" }}>significance</span>.
            </blockquote>
            {/* Bottom hairline */}
            <span
              aria-hidden
              className="block h-px"
              style={{ width: 80, backgroundColor: "rgba(201,168,76,0.3)" }}
            />
          </div>
        </Reveal>
      </section>

      {/* The Process — replaces the old stats */}
      <section
        data-testid="process-section"
        className="relative border-y border-brand-text/[0.07]"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-surface/30 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:px-10 sm:py-24">
          <Reveal>
            <p
              data-testid="process-eyebrow"
              className="text-center font-mono"
              style={{
                fontWeight: 500,
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.32em",
                color: "#C9A84C",
              }}
            >
              —— The Process
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-[rgba(201,168,76,0.15)]">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div
                  data-testid={`process-step-${p.n}`}
                  className="flex flex-col items-start gap-4 px-0 lg:px-10"
                >
                  <p
                    className="font-display font-normal"
                    style={{
                      fontSize: "clamp(28px, 3vw, 44px)",
                      color: "#C9A84C",
                      letterSpacing: "-0.02em",
                      lineHeight: 1,
                    }}
                  >
                    {p.n} —
                  </p>
                  <p
                    className="select-none"
                    style={{
                      fontFamily: "Inter, system-ui, sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      textTransform: "uppercase",
                      letterSpacing: "0.3em",
                      color: "#F5F0E8",
                    }}
                  >
                    {p.label}
                  </p>
                  <p
                    style={{
                      fontFamily: "Inter, system-ui, sans-serif",
                      fontWeight: 400,
                      fontSize: 15,
                      lineHeight: 1.5,
                      color: "rgba(245,240,232,0.78)",
                    }}
                  >
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section data-testid="about-section" className="relative mx-auto max-w-5xl px-6 py-32 sm:px-10 sm:py-40 text-center">
        <Reveal>
          <Eyebrow index="01">About the Agency</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mx-auto mt-8 font-display font-normal text-brand-text text-balance"
              style={{ fontSize: "clamp(40px, 5.6vw, 80px)", letterSpacing: "-0.035em", lineHeight: 1.04 }}>
            Management built around <em className="italic text-brand-gold">the creator.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mx-auto mt-10 max-w-2xl text-base leading-relaxed text-brand-text/65 sm:text-lg">
            An Atlanta-based talent management and digital marketing agency built for the modern creator economy. We represent a selective roster of creators and provide the full operating stack — strategy, content direction, fan-engagement operations, paid and organic growth, brand partnerships — engineered to optimize monthly retention and long-term revenue. Every client engagement is confidential by policy.
          </p>
        </Reveal>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="relative">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32">
          <Reveal><Eyebrow index="02">Services</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-display font-normal text-brand-text text-balance"
                style={{ fontSize: "clamp(40px, 5.6vw, 80px)", letterSpacing: "-0.035em", lineHeight: 1.04 }}>
              What we <em className="italic text-brand-gold">run</em> for clients.
            </h2>
          </Reveal>

          <div className="mt-20 grid gap-6 sm:grid-cols-2 lg:gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <motion.article
                  data-testid={`service-${s.n}`}
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  style={{ backgroundColor: "#1A1A1A" }}
                  className="relative isolate overflow-hidden rounded-3xl border border-[rgba(201,168,76,0.2)] p-9 transition-[border-color] duration-300 hover:border-[rgba(201,168,76,0.6)] lg:p-10"
                >
                  <CornerBrackets size={20} />
                  <p className="font-display text-3xl font-normal text-brand-gold" style={{ letterSpacing: "-0.02em" }}>{s.n}</p>
                  <h3 className="mt-6 font-display text-2xl font-normal text-brand-text sm:text-3xl" style={{ letterSpacing: "-0.02em" }}>{s.title}</h3>
                  <p
                    data-testid={`service-${s.n}-subhead`}
                    className="mt-2 font-display italic text-brand-text/70"
                    style={{ fontSize: "clamp(15px, 1.4vw, 18px)", letterSpacing: "-0.01em" }}
                  >
                    {s.subhead}
                  </p>
                  <DrawRule className="mt-4" width="w-10" />
                  <p className="mt-5 text-sm leading-relaxed text-brand-text/65 sm:text-base">{s.body}</p>
                </motion.article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative border-t border-brand-text/[0.07]">
        <div className="relative mx-auto max-w-4xl px-6 py-32 text-center sm:px-10 sm:py-40">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-brand-gold">By Invitation Only</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mx-auto mt-8 font-display font-normal text-brand-text text-balance"
                style={{ fontSize: "clamp(44px, 6.5vw, 96px)", letterSpacing: "-0.04em", lineHeight: 1.02 }}>
              The room is <em className="italic text-brand-gold">private.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-8 max-w-md text-sm text-brand-text/55 sm:text-base">
              A response, if extended, arrives within forty-eight hours.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Button as={Link} to="/apply" data-testid="footer-apply-cta" variant="outline-gold" size="lg" className="mt-12">
              Request an Invitation
            </Button>
          </Reveal>
        </div>
      </section>

      <BrandFooter />
    </main>
  );
}
